import { useEffect, useRef } from "react"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"
import { TextStyle } from "@tiptap/extension-text-style"
import Placeholder from "@tiptap/extension-placeholder"
import ImageResize from "tiptap-extension-resize-image"
import { Node, mergeAttributes } from "@tiptap/core"
import "./styles.css"

const FileNode = Node.create({
  name: "file",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return {
      href: { default: null },
      name: { default: "Document" },
      target: { default: "_blank" },
      rel: { default: "noopener noreferrer nofollow" },
    }
  },

  parseHTML() {
    return [
      { tag: "a[data-file]" },
      { tag: "a[href]", getAttrs: (node) => node.getAttribute("href") && { href: node.getAttribute("href") } },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "a",
      mergeAttributes(HTMLAttributes, {
        "data-file": "",
        target: "_blank",
        rel: "noopener noreferrer nofollow",
        href: HTMLAttributes.href,
        style: "color: #0066cc; text-decoration: underline; cursor: pointer;",
      }),
      HTMLAttributes.name || "Document",
    ]
  },

  addCommands() {
    return {
      setFile:
        (options) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: options,
            })
            .run()
        },
    }
  },
})

export const DescriptionEditor = ({ value, onChange, setFieldValue, values, initialData, isEditMode }) => {
  console.log("[v0] Editor values:", value, values)
  const imageInputRef = useRef(null)
  const docInputRef = useRef(null)

  const transformContentForEditor = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return htmlContent

    // Parse the HTML so we can safely manipulate nodes
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, "text/html")

    // Transform images: apply containerstyle and wrapperstyle like renderDescriptionHtml
    doc.querySelectorAll("img").forEach((img) => {
      // Move custom 'containerstyle' to the real 'style' on the <img>
      const containerStyle = img.getAttribute("containerstyle") || ""
      if (containerStyle) {
        const existing = img.getAttribute("style") || ""
        const merged = [existing, containerStyle].filter(Boolean).join("; ")
        img.setAttribute("style", merged)
      }

      // If we have a 'wrapperstyle', create a real wrapper <div> and put the <img> inside it
      const wrapperStyle = img.getAttribute("wrapperstyle") || ""
      img.removeAttribute("containerstyle")
      img.removeAttribute("wrapperstyle")
      img.removeAttribute("data-resizable")

      if (wrapperStyle) {
        const wrapper = doc.createElement("div")
        wrapper.setAttribute("style", wrapperStyle)

        // Insert wrapper before img, then move img inside wrapper
        const parent = img.parentNode
        if (parent) {
          parent.insertBefore(wrapper, img)
          wrapper.appendChild(img)
        }
      } else {
        // If no wrapperstyle but the img relies on margin auto to align,
        // ensure it sits inside a flex wrapper so margin-left:auto works.
        if (/margin\s*:\s*[^;]*auto/.test(containerStyle)) {
          const wrapper = doc.createElement("div")
          wrapper.setAttribute("style", "display:flex;")
          const parent = img.parentNode
          if (parent) {
            parent.insertBefore(wrapper, img)
            wrapper.appendChild(img)
          }
        }
      }
    })

    return doc.body.innerHTML
  }

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer nofollow",
        },
      }),
      Placeholder.configure({ placeholder: "Write your content here..." }),
      // Image.configure({
      //   inline: false,
      //   allowBase64: true,
      //   HTMLAttributes: {
      //     style: "max-width: 100%; height: auto; cursor: pointer;",
      //     "data-resizable": "true",
      //   },
      // }),
      ImageResize.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          style: "max-width: 100%; height: auto;",
        },
      }),
      FileNode,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none",
      },
      // handleClickOn: (view, pos, node, nodePos, event, direct) => {
      //   console.log("[v0] Click detected on:", node.type.name, node.attrs)
      //   if (node.type.name === "image") {
      //     console.log("[v0] Image clicked at position:", pos, "Node attrs:", node.attrs)
      //     view.dispatch(
      //       view.state.tr.setSelection(view.state.selection.constructor.create(view.state.doc, nodePos, nodePos + 1)),
      //     )
      //     return true
      //   }
      //   if (node.type.name === "file") {
      //     console.log("[v0] File link clicked:", node.attrs.href)
      //     if (node.attrs.href) {
      //       window.open(node.attrs.href, "_blank")
      //       return true
      //     }
      //   }
      //   return false
      // },
    },
  })

  useEffect(() => {
    if (editor && value && value !== editor.getHTML()) {
      console.log("[v0] Setting editor content:", value)
      const transformedContent = transformContentForEditor(value)
      console.log("[v0] Transformed content:", transformedContent)
      editor.commands.setContent(transformedContent, false)
    }
  }, [editor, value])

  // useEffect(() => {
  //   if (editor && isEditMode) {
  //     console.log("[v0] Edit mode activated")
  //     setTimeout(() => {
  //       const currentContent = editor.getHTML()
  //       console.log("[v0] Current content in edit mode:", currentContent)

  //       const normalizedContent = currentContent
  //         .replace(/containerstyle="[^"]*"/g, "")
  //         .replace(/wrapperstyle="[^"]*"/g, "")
  //         .replace(/<img([^>]*?)>/g, (match, attrs) => {
  //           if (!attrs.includes("data-resizable")) {
  //             attrs += ' data-resizable="true"'
  //           }
  //           if (!attrs.includes("style=")) {
  //             attrs += ' style="max-width: 100%; height: auto; cursor: pointer;"'
  //           }
  //           return `<img${attrs}>`
  //         })

  //       console.log("[v0] Normalized content:", normalizedContent)
  //       editor.commands.setContent(normalizedContent, false)
  //       editor.commands.focus()
  //     }, 300)
  //   }
  // }, [editor, isEditMode])

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files)

    const newImages = files.map((file) => {
      const blobUrl = URL.createObjectURL(file)

      editor
        .chain()
        .focus()
        .setImage({
          src: blobUrl,
          alt: file.name,
        })
        .run()

      return {
        file,
        blobUrl,
        isExisting: false,
        toRemove: false,
      }
    })

    setFieldValue("images", [...(values?.images || []), ...newImages])
  }

  const handleDocUpload = (e) => {
    const files = Array.from(e.target.files)

    const newDocuments = files.map((file) => {
      const blobUrl = URL.createObjectURL(file)

      editor
        .chain()
        .focus()
        .setFile({
          href: blobUrl,
          name: file.name,
        })
        .run()

      return {
        file,
        blobUrl,
        name: file.name,
        isExisting: false,
        toRemove: false,
      }
    })

    setFieldValue("documents", [...(values?.documents || []), ...newDocuments])
  }

  if (!editor) return null

  return (
    <div className="tiptap-wrapper">
      <div className="editor-toolbar">
        <select
          onChange={(e) => {
            const level = e.target.value
            if (level === "paragraph") {
              editor.chain().focus().setParagraph().run()
            } else {
              editor
                .chain()
                .focus()
                .toggleHeading({ level: Number.parseInt(level) })
                .run()
            }
          }}
          value={
            editor.isActive("heading", { level: 1 })
              ? "1"
              : editor.isActive("heading", { level: 2 })
                ? "2"
                : editor.isActive("heading", { level: 3 })
                  ? "3"
                  : "paragraph"
          }
          className="editor-dropdown"
        >
          <option value="paragraph">Normal</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? "active" : ""}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? "active" : ""}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive("underline") ? "active" : ""}
        >
          <u>U</u>
        </button>
        <button
          type="button"
          onClick={() => {
            const url = prompt("Enter URL")
            if (url) editor.chain().focus().setLink({ href: url }).run()
          }}
        >
          <i className="bi bi-link-45deg" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? "active" : ""}
        >
          •
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive("orderedList") ? "active" : ""}
        >
          1.
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive("strike") ? "active" : ""}
        >
          <s>abc</s>
        </button>

        <button type="button" onClick={() => imageInputRef.current.click()}>
          <i className="bi bi-image"></i> Image
        </button>
        <input
          type="file"
          accept="image/*"
          multiple
          ref={imageInputRef}
          style={{ display: "none" }}
          onChange={handleImageUpload}
        />

        <button type="button" onClick={() => docInputRef.current.click()}>
          <i className="bi bi-paperclip"></i> Doc
        </button>
        <input
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx"
          multiple
          ref={docInputRef}
          style={{ display: "none" }}
          onChange={handleDocUpload}
        />
      </div>

      <EditorContent editor={editor} />
    </div>
  )
}
